'use client';
import { usePortalContainer } from 'v5/primitives/PortalContainer';

import { Portal } from '@radix-ui/react-portal';
import { Slot } from '@radix-ui/react-slot';
import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useRef,
  useState
} from 'react';
import { isPointerEventFromTileInteractiveControl } from 'v5/patterns/entity-list/gridTileInteractiveTargets';
import {
  type Box,
  boxBetween,
  boxesMeet,
  edgeScrollSpeed,
  marqueeModeOf,
  marqueeSelection,
  type Point
} from './marqueeGeometry';

/** From empty space the rectangle starts at once; from an item a click that wobbles stays a click. */
const THRESHOLD_FROM_EMPTY_SPACE = 4;
const THRESHOLD_FROM_ITEM = 25;

const NOT_TYPED_INTO = [
  'button',
  'checkbox',
  'color',
  'file',
  'image',
  'radio',
  'range',
  'reset',
  'submit'
];

type Keys = { shiftKey: boolean; ctrlKey: boolean; metaKey: boolean };

type Gesture = {
  pointerId: number;
  scroller: Element;
  pressedAt: Point;
  /** In the scroller's content coordinates, so a scroll moves the rectangle's far corner only. */
  anchor: Point;
  pointer: Point;
  keys: Keys;
  threshold: number;
  pressedEmptySpace: boolean;
  started: boolean;
  initial: string[];
  emitted: string[];
  /** The mounted items' boxes in content coordinates, kept for those scrolled away and unmounted. */
  seen: Map<string, Box>;
  frame: number | null;
  steppedAt: number | null;
  stop: () => void;
};

const isDocumentScroller = (element: Element) =>
  element === document.scrollingElement;

const canScroll = (element: Element, axis: 'x' | 'y') => {
  const overflows =
    axis === 'y'
      ? element.scrollHeight > element.clientHeight
      : element.scrollWidth > element.clientWidth;
  if (isDocumentScroller(element)) return overflows;
  const overflow =
    getComputedStyle(element)[axis === 'y' ? 'overflowY' : 'overflowX'];
  return overflows && /auto|scroll|overlay/.test(overflow);
};

export const scrollerOf = (from: Element) => {
  for (
    let element: Element | null = from;
    element &&
    element !== document.body &&
    element !== document.documentElement;
    element = element.parentElement
  ) {
    if (canScroll(element, 'y') || canScroll(element, 'x')) return element;
  }
  return document.scrollingElement ?? document.documentElement;
};

/** The scroller's visible area, in client coordinates. */
export const viewportOf = (scroller: Element): Box => {
  if (isDocumentScroller(scroller)) {
    return {
      left: 0,
      top: 0,
      right: document.documentElement.clientWidth,
      bottom: document.documentElement.clientHeight
    };
  }
  const box = scroller.getBoundingClientRect();
  const left = box.left + scroller.clientLeft;
  const top = box.top + scroller.clientTop;
  return {
    left,
    top,
    right: left + scroller.clientWidth,
    bottom: top + scroller.clientHeight
  };
};

const isOnScrollBar = (target: Element, point: Point) => {
  if (!canScroll(target, 'y') && !canScroll(target, 'x')) return false;
  const box = target.getBoundingClientRect();
  return (
    point.x - box.left - target.clientLeft >= target.clientWidth ||
    point.y - box.top - target.clientTop >= target.clientHeight
  );
};

const isTypedInto = (element: Element | null) =>
  element instanceof HTMLTextAreaElement ||
  (element instanceof HTMLElement && element.isContentEditable) ||
  (element instanceof HTMLInputElement &&
    !NOT_TYPED_INTO.includes(element.type));

const keysOf = (event: KeyboardEvent | PointerEvent): Keys => ({
  shiftKey: event.shiftKey,
  ctrlKey: event.ctrlKey,
  metaKey: event.metaKey
});

const sameIds = (a: string[], b: string[]) => {
  const set = new Set(a);
  return a.length === b.length && b.every(id => set.has(id));
};

const SelectedItemContext = createContext<string[]>([]);

export function useSelectionLayout() {
  return useContext(SelectedItemContext);
}

/**
 * Marquee selection over the `[data-item-id]` elements inside its child: a drag draws a
 * rectangle that picks what it covers as it goes (Shift adds, Cmd or Ctrl flips), scrolls the box
 * under the pointer by a wheel or by holding the pointer at its edge, and ends on release, on a
 * cancelled pointer, or when the window loses focus. Escape clears the picks of the layout last
 * pressed or focused, unless a text field has the focus.
 */
export function SelectionLayout({
  children,
  selectedIds,
  onSelectedIdsChange,
  enabled = true,
  itemsDrag = false,
  ignoresHandledEscape = false,
  collectionRootIsShell = false,
  itemsInRect
}: {
  children?: ReactNode;
  selectedIds: string[];
  onSelectedIdsChange: (selectedIds: string[]) => void;
  /** Off: no marquee and Escape keeps the selection, with the children still mounted. */
  enabled?: boolean;
  /**
   * The items drag natively (React Aria drag and drop), with a mouse only: a drag from an item is
   * its own (it moves the item, or the selection when the item is selected). The marquee starts
   * from empty space, or from an item with Shift, Ctrl or Cmd held, and a click on empty space
   * clears the selection.
   */
  itemsDrag?: boolean;
  /** An Escape something else already handled (a dialog closing) keeps the selection. */
  ignoresHandledEscape?: boolean;
  /**
   * The marquee also starts in a React Aria collection no item has focused yet, whose root is then
   * the tab stop.
   */
  collectionRootIsShell?: boolean;
  /**
   * The ids of the items whose boxes meet `rect`, in client coordinates at the moment of the call
   * (it may reach past the viewport, over what is scrolled away). A virtualized host answers from
   * its layout, so rows never mounted during the drag count too. Without it, the mounted
   * `[data-item-id]` elements are read, and those scrolled away during the drag are remembered.
   */
  itemsInRect?: (rect: Box) => string[];
}) {
  const portalContainer = usePortalContainer();
  const [pressed, setPressed] = useState(false);
  const [drawn, setDrawn] = useState<Box | null>(null);

  const containerRef = useRef<HTMLDivElement>(null);
  const gestureRef = useRef<Gesture | null>(null);
  const pressedInsideRef = useRef(false);
  const latest = useRef({
    selectedIds,
    onSelectedIdsChange,
    itemsDrag,
    ignoresHandledEscape,
    itemsInRect
  });
  latest.current = {
    selectedIds,
    onSelectedIdsChange,
    itemsDrag,
    ignoresHandledEscape,
    itemsInRect
  };

  const emit = (gesture: Gesture, ids: string[]) => {
    if (sameIds(ids, gesture.emitted)) return;
    gesture.emitted = ids;
    latest.current.onSelectedIdsChange(ids);
  };

  const toContent = (gesture: Gesture, point: Point): Point => {
    const viewport = viewportOf(gesture.scroller);
    return {
      x: point.x - viewport.left + gesture.scroller.scrollLeft,
      y: point.y - viewport.top + gesture.scroller.scrollTop
    };
  };

  const coveredIds = (gesture: Gesture, rect: Box) => {
    const { itemsInRect } = latest.current;
    const viewport = viewportOf(gesture.scroller);
    const dx = gesture.scroller.scrollLeft - viewport.left;
    const dy = gesture.scroller.scrollTop - viewport.top;
    if (itemsInRect) {
      return itemsInRect({
        left: rect.left - dx,
        top: rect.top - dy,
        right: rect.right - dx,
        bottom: rect.bottom - dy
      });
    }
    containerRef.current
      ?.querySelectorAll<HTMLElement>('[data-item-id]')
      .forEach(element => {
        const id = element.dataset.itemId;
        if (!id) return;
        const box = element.getBoundingClientRect();
        gesture.seen.set(id, {
          left: box.left + dx,
          top: box.top + dy,
          right: box.right + dx,
          bottom: box.bottom + dy
        });
      });
    return [...gesture.seen]
      .filter(([, box]) => boxesMeet(box, rect))
      .map(([id]) => id);
  };

  /** Picks what the rectangle covers; returns the rectangle in content coordinates. */
  const pick = (gesture: Gesture) => {
    const rect = boxBetween(
      gesture.anchor,
      toContent(gesture, gesture.pointer)
    );
    emit(
      gesture,
      marqueeSelection(
        marqueeModeOf(gesture.keys),
        gesture.initial,
        coveredIds(gesture, rect)
      )
    );
    return rect;
  };

  /** One frame: scroll by the edge, pick and draw the rectangle clipped to the scrolling box. */
  const step = (gesture: Gesture) => {
    gesture.frame = null;
    if (!gesture.started) return;
    const { scroller } = gesture;
    const viewport = viewportOf(scroller);
    const before = { left: scroller.scrollLeft, top: scroller.scrollTop };
    // the speed is per 60 Hz frame: a faster display takes smaller steps
    const now = performance.now();
    const frames = Math.min(
      3,
      (now - (gesture.steppedAt ?? now - 16.7)) / 16.7
    );
    gesture.steppedAt = now;
    // whole pixels, at least one: a smaller step rounds away and would stop the scrolling
    const scrollBy = (speed: number) =>
      speed === 0
        ? 0
        : Math.sign(speed) * Math.max(1, Math.round(Math.abs(speed) * frames));
    if (canScroll(scroller, 'x')) {
      scroller.scrollLeft += scrollBy(
        edgeScrollSpeed(gesture.pointer.x, viewport.left, viewport.right)
      );
    }
    if (canScroll(scroller, 'y')) {
      scroller.scrollTop += scrollBy(
        edgeScrollSpeed(gesture.pointer.y, viewport.top, viewport.bottom)
      );
    }
    const rect = pick(gesture);
    const dx = viewport.left - scroller.scrollLeft;
    const dy = viewport.top - scroller.scrollTop;
    setDrawn({
      left: Math.max(rect.left + dx, viewport.left),
      top: Math.max(rect.top + dy, viewport.top),
      right: Math.min(rect.right + dx, viewport.right),
      bottom: Math.min(rect.bottom + dy, viewport.bottom)
    });
    // at the edge the next frame scrolls on, until the box can't
    if (
      scroller.scrollLeft !== before.left ||
      scroller.scrollTop !== before.top
    ) {
      schedule(gesture);
    }
  };

  const schedule = (gesture: Gesture) => {
    if (gesture.frame === null) {
      gesture.frame = requestAnimationFrame(() => step(gesture));
    }
  };

  /**
   * `release` keeps what was drawn, or clears on a click on empty space; `keep` keeps what was
   * drawn; `cancel` restores the picks from before the press.
   */
  const end = (
    outcome: 'release' | 'keep' | 'cancel',
    target?: EventTarget | null
  ) => {
    const gesture = gestureRef.current;
    if (!gesture) return;
    gestureRef.current = null;
    gesture.stop();
    if (gesture.frame !== null) cancelAnimationFrame(gesture.frame);
    setPressed(false);
    setDrawn(null);
    const container = containerRef.current;
    if (container?.hasPointerCapture(gesture.pointerId)) {
      container.releasePointerCapture(gesture.pointerId);
    }
    if (!gesture.started) {
      if (
        outcome === 'release' &&
        gesture.pressedEmptySpace &&
        (latest.current.itemsDrag || target === container)
      ) {
        latest.current.onSelectedIdsChange([]);
      }
      return;
    }
    switch (outcome) {
      case 'release':
        pick(gesture);
        window.getSelection()?.removeAllRanges();
        // the click that follows a release over the same element would open or pick it
        swallowNextClick();
        return;
      case 'keep':
        return;
      case 'cancel':
        emit(gesture, gesture.initial);
    }
  };

  useEffect(() => {
    if (!enabled) return;
    const notePress = (event: PointerEvent) => {
      pressedInsideRef.current =
        event.target instanceof Node &&
        !!containerRef.current?.contains(event.target);
    };
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      if (latest.current.ignoresHandledEscape && event.defaultPrevented) return;
      const focused = document.activeElement;
      if (isTypedInto(focused)) return;
      const ours =
        focused && focused !== document.body
          ? !!containerRef.current?.contains(focused)
          : pressedInsideRef.current;
      if (!ours && !gestureRef.current) return;
      end('keep');
      latest.current.onSelectedIdsChange([]);
    };
    window.addEventListener('pointerdown', notePress, true);
    document.addEventListener('keydown', handleEscape);
    return () => {
      window.removeEventListener('pointerdown', notePress, true);
      document.removeEventListener('keydown', handleEscape);
      end('keep');
    };
    // `end` reads refs only
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled]);

  const handlePointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    if (!enabled) return;
    end('keep');
    const container = containerRef.current;
    const target = event.target;
    if (
      !container ||
      !(target instanceof Element) ||
      !container.contains(target)
    ) {
      return;
    }
    if (
      isPointerEventFromTileInteractiveControl(target, {
        collectionRootIsShell
      })
    ) {
      return;
    }
    // Alt keeps the browser's text selection
    if (event.altKey || event.button !== 0) return;
    // a finger or a pen scrolls the board or swipes a card's pictures: no lasso there
    if (itemsDrag && event.pointerType !== 'mouse') return;
    const pressedAt = { x: event.clientX, y: event.clientY };
    if (isOnScrollBar(target, pressedAt)) return;
    const item = target.closest('[data-item-id]');
    // an item's selection circle draws the rectangle, as empty space does; a finger there scrolls
    const fromCircle =
      event.pointerType === 'mouse' &&
      !!item?.contains(target.closest('[data-marquee-start]'));
    const withModifier = event.shiftKey || event.ctrlKey || event.metaKey;
    // a drag from an item is the item's, unless a modifier or its circle asks for the rectangle
    const fromItemBody = item !== null && !withModifier && !fromCircle;
    if (itemsDrag && fromItemBody) return;

    const scroller = scrollerOf(item?.parentElement ?? target);
    const gesture: Gesture = {
      pointerId: event.pointerId,
      scroller,
      pressedAt,
      anchor: { x: 0, y: 0 },
      pointer: pressedAt,
      keys: keysOf(event.nativeEvent),
      threshold: fromItemBody
        ? THRESHOLD_FROM_ITEM
        : THRESHOLD_FROM_EMPTY_SPACE,
      pressedEmptySpace: itemsDrag
        ? item === null && !withModifier
        : target === container,
      started: false,
      initial: selectedIds,
      emitted: selectedIds,
      seen: new Map(),
      frame: null,
      steppedAt: null,
      stop: () => {}
    };
    gesture.anchor = toContent(gesture, pressedAt);

    const ownPointer = (e: PointerEvent) => e.pointerId === gesture.pointerId;
    const onMove = (e: PointerEvent) => {
      if (!ownPointer(e)) return;
      // the release happened where no event reached the page
      if (e.buttons === 0) return end('keep');
      if (e.altKey) return end('cancel');
      gesture.pointer = { x: e.clientX, y: e.clientY };
      gesture.keys = keysOf(e);
      if (!gesture.started) {
        const distance = Math.hypot(
          e.clientX - gesture.pressedAt.x,
          e.clientY - gesture.pressedAt.y
        );
        if (distance < gesture.threshold) return;
        gesture.started = true;
        try {
          container.setPointerCapture(gesture.pointerId);
        } catch {
          // a pointer the browser no longer tracks: the window still hears it
        }
      }
      schedule(gesture);
    };
    const onUp = (e: PointerEvent) => {
      if (!ownPointer(e)) return;
      gesture.pointer = { x: e.clientX, y: e.clientY };
      gesture.keys = keysOf(e);
      end('release', e.target);
    };
    const onCancel = (e: PointerEvent) => {
      if (ownPointer(e)) end('cancel');
    };
    const onKeys = (e: KeyboardEvent) => {
      if (e.key === 'Alt' && e.type === 'keydown') return end('cancel');
      // while drawing, Escape takes the rectangle back, as it does a drag; the collection under
      // it (React Aria clears its selection on Escape) doesn't hear it
      if (e.key === 'Escape' && e.type === 'keydown' && gesture.started) {
        e.preventDefault();
        e.stopPropagation();
        return end('cancel');
      }
      gesture.keys = keysOf(e);
      schedule(gesture);
    };
    const onScroll = () => schedule(gesture);
    const onLeave = () => end('keep');
    // in capture, before React Aria's handler, which starts no drag when it is prevented
    const cancelItemDrag = (e: DragEvent) => {
      if (latest.current.itemsDrag) e.preventDefault();
    };
    // a native drag that starts after all (a picture, a link) takes the pointer
    const onDragStart = (e: DragEvent) => {
      if (!e.defaultPrevented) end('cancel');
    };
    const scrollEvents = isDocumentScroller(scroller) ? window : scroller;
    window.addEventListener('pointermove', onMove, true);
    window.addEventListener('pointerup', onUp, true);
    window.addEventListener('pointercancel', onCancel, true);
    window.addEventListener('keydown', onKeys, true);
    window.addEventListener('keyup', onKeys, true);
    window.addEventListener('blur', onLeave);
    document.addEventListener('visibilitychange', onLeave);
    container.addEventListener('dragstart', cancelItemDrag, true);
    window.addEventListener('dragstart', onDragStart);
    scrollEvents.addEventListener('scroll', onScroll);
    gesture.stop = () => {
      window.removeEventListener('pointermove', onMove, true);
      window.removeEventListener('pointerup', onUp, true);
      window.removeEventListener('pointercancel', onCancel, true);
      window.removeEventListener('keydown', onKeys, true);
      window.removeEventListener('keyup', onKeys, true);
      window.removeEventListener('blur', onLeave);
      document.removeEventListener('visibilitychange', onLeave);
      container.removeEventListener('dragstart', cancelItemDrag, true);
      window.removeEventListener('dragstart', onDragStart);
      scrollEvents.removeEventListener('scroll', onScroll);
    };
    gestureRef.current = gesture;
    setPressed(true);
  };

  return (
    <>
      <SelectedItemContext.Provider value={selectedIds}>
        <Slot
          ref={containerRef}
          onPointerDownCapture={handlePointerDown}
          style={{
            userSelect: pressed ? 'none' : 'auto',
            WebkitUserSelect: pressed ? 'none' : 'auto'
          }}
        >
          {children}
        </Slot>
      </SelectedItemContext.Provider>
      {drawn && (
        <Portal container={portalContainer}>
          <div
            className="z-1000 fixed border-bg-emphasis/50 border-dashed border rounded-md bg-bg-emphasis/20 pointer-events-none mix-blend-exclusion"
            style={{
              left: drawn.left,
              top: drawn.top,
              width: Math.max(0, drawn.right - drawn.left),
              height: Math.max(0, drawn.bottom - drawn.top)
            }}
          />
        </Portal>
      )}
    </>
  );
}

function swallowNextClick() {
  const swallow = (event: MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
  };
  window.addEventListener('click', swallow, { capture: true, once: true });
  setTimeout(() => window.removeEventListener('click', swallow, true), 0);
}
